/**
 * Unified Site Survey Progress Service
 *
 * This service provides a single source of truth for calculating site survey progress.
 * It ensures consistency across all UI components and properly handles survey completion.
 */

export interface RequiredField {
  field: string;
  label: string;
  section: string;
  required: boolean;
}

export interface ProgressResult {
  percentage: number;
  completedItems: number;
  totalItems: number;
  missingFields: string[];
  canComplete: boolean;
  sectionProgress: Record<string, boolean>;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  missingFields: string[];
}

export class SiteSurveyProgressService {
  private requiredFields: RequiredField[] = [
    // Survey Header (Project Tab) - Core identification fields
    { field: 'siteAssessorName', label: 'Site Assessor Name', section: 'project', required: true },
    { field: 'surveyDate', label: 'Survey Date', section: 'project', required: true },
    { field: 'clientName', label: 'Client Name', section: 'project', required: true },
    { field: 'clientAddress', label: 'Client Address', section: 'project', required: true },

    // Electrical Supply Information - Essential electrical details
    { field: 'phases', label: 'Phases', section: 'electrical', required: true },
    { field: 'mainFuseSize', label: 'Main Fuse Size', section: 'electrical', required: true },
    { field: 'earthingSystem', label: 'Earthing System', section: 'electrical', required: true },
    // Note: acSpdPresent is boolean in DB, not suitable as required field

    // Roof Information - Basic roof assessment
    { field: 'roofs', label: 'Roof Details', section: 'roof', required: true },
    { field: 'scaffolding', label: 'Scaffolding Requirements', section: 'roof', required: true },

    // AC Installation - Installation planning
    { field: 'inverterDb', label: 'Inverter DB Location', section: 'ac-equipment', required: true },
    {
      field: 'proposedArea',
      label: 'Proposed Equipment Area',
      section: 'ac-equipment',
      required: true,
    },
    // Note: Removed numeric fields (equipmentHeight, equipmentWidth) as they default to 0
    // Note: acSize and switchFuseRequired are important but may be determined later

    // DC Additional Works - Basic DC requirements
    { field: 'dcStrings', label: 'DC String Configuration', section: 'dc-works', required: true },
    { field: 'dcCableCsa', label: 'DC Cable Size', section: 'dc-works', required: true },
    { field: 'dcLongestRun', label: 'DC Cable Run Length', section: 'dc-works', required: true },
    // Note: gatewayEarthing might not always be applicable
  ];

  /**
   * Helper function to check if a field is filled with enhanced validation
   */
  private isFieldFilled(value: any, fieldName?: string): boolean {
    // Debug logging for Survey Date field
    if (fieldName === 'surveyDate') {
      console.log('[Progress Service] Survey Date validation:', {
        fieldName,
        value,
        valueType: typeof value,
        isNull: value === null,
        isUndefined: value === undefined,
        isDate: value instanceof Date,
        dateString: value ? String(value) : 'N/A',
      });
    }

    // Null or undefined are not filled
    if (value === null || value === undefined) return false;

    // Enhanced string validation for text-based fields
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Empty string is not filled
      if (trimmed.length === 0) return false;

      // Special handling for text-based fields that replaced boolean fields
      const textBooleanFields = ['bondingWater', 'bondingGas', 'acSpdPresent', 'eicr'];
      if (fieldName && textBooleanFields.includes(fieldName)) {
        // These fields should have valid text values like "YES", "NO", "REQUIRED", "N/A", etc.
        const validValues = [
          'YES',
          'NO',
          'REQUIRED',
          'N/A',
          'NOT REQUIRED',
          'PRESENT',
          'NOT PRESENT',
        ];
        return validValues.some((valid) => trimmed.toUpperCase().includes(valid.toUpperCase()));
      }

      // Default placeholder values are not filled
      if (trimmed === 'N/A' || trimmed === 'n/a') return false;
      return true;
    }

    // Numbers must be valid and not zero (for most fields, zero means not filled)
    if (typeof value === 'number') {
      return !isNaN(value) && value > 0; // Must be positive number
    }

    // Enhanced boolean handling - these are now primarily for checkbox fields
    if (typeof value === 'boolean') {
      // For checkbox fields that default to false, only true counts as "filled"
      const checkboxFields = ['evChargerRequired', 'immersionCircuitRequired', 'birdguardRequired'];
      if (fieldName && checkboxFields.includes(fieldName)) {
        return value === true; // Only true counts as actively selected
      }

      // Old boolean fields have been replaced with text fields, so boolean shouldn't be considered filled
      // unless it's a known checkbox field
      return false;
    }

    // Enhanced array validation
    if (Array.isArray(value)) {
      if (value.length === 0) return false;

      // Special handling for roof arrays - they should have meaningful roof data
      if (fieldName === 'roofs') {
        return value.some((roof) => {
          return (
            roof &&
            typeof roof === 'object' &&
            (roof.roofType ||
              roof.roofMaterial ||
              roof.roofOrientation ||
              roof.roofPitch ||
              roof.roofArea)
          );
        });
      }

      // Special handling for inverter circuits
      if (fieldName === 'inverterCircuits') {
        return value.some((circuit) => {
          return (
            circuit &&
            typeof circuit === 'object' &&
            (circuit.circuitType || circuit.cableSize || circuit.length)
          );
        });
      }

      // General array validation - check if items have actual content
      if (value.length === 1 && typeof value[0] === 'object') {
        const hasContent = Object.keys(value[0]).some((key) => {
          const itemValue = value[0][key];
          return itemValue !== undefined && itemValue !== null && itemValue !== '';
        });
        return hasContent;
      }
      return value.length > 0;
    }

    // Enhanced object validation
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return false;

      // Check if object has any meaningful values
      return keys.some((key) => {
        const propValue = value[key];
        if (propValue === undefined || propValue === null || propValue === '') return false;

        // Recursive check for nested objects
        if (typeof propValue === 'object' && !Array.isArray(propValue)) {
          return this.isFieldFilled(propValue, key);
        }

        return true;
      });
    }

    return false;
  }

  /**
   * Calculate overall progress based on required fields
   */
  calculateProgress(formData: Record<string, any>): ProgressResult {
    const requiredFieldsOnly = this.requiredFields.filter((f) => f.required);
    const totalItems = requiredFieldsOnly.length;
    const missingFields: string[] = [];
    const sectionProgress: Record<string, boolean> = {};

    // Track completed sections
    const sectionFields: Record<string, RequiredField[]> = {};
    requiredFieldsOnly.forEach((field) => {
      if (!sectionFields[field.section]) {
        sectionFields[field.section] = [];
      }
      sectionFields[field.section].push(field);
    });

    // Check each required field
    let completedItems = 0;
    requiredFieldsOnly.forEach((field) => {
      const value = formData[field.field];
      const isFilled = this.isFieldFilled(value, field.field);

      // Debug logging for ALL required fields
      if (!isFilled) {
        console.log(`[Progress Service] Field NOT filled: ${field.field} (${field.label})`, {
          value,
          valueType: typeof value,
          section: field.section,
        });
      }

      if (isFilled) {
        completedItems++;
      } else {
        missingFields.push(field.label);
      }
    });

    // Calculate section progress
    Object.entries(sectionFields).forEach(([section, fields]) => {
      const sectionComplete = fields.every((field) =>
        this.isFieldFilled(formData[field.field], field.field)
      );
      sectionProgress[section] = sectionComplete;
    });

    // Summary section is complete if all other sections are complete
    sectionProgress['summary'] = Object.entries(sectionProgress)
      .filter(([key]) => key !== 'summary')
      .every(([, complete]) => complete);

    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    return {
      percentage,
      completedItems,
      totalItems,
      missingFields,
      canComplete: percentage === 100,
      sectionProgress,
    };
  }

  /**
   * Check if a specific section is complete
   */
  isSectionComplete(sectionId: string, formData: Record<string, any>): boolean {
    // Direct mapping - tab IDs match section names in requiredFields
    const section = sectionId;

    // Summary is complete if all required fields are filled
    if (section === 'summary') {
      const progress = this.calculateProgress(formData);
      return progress.percentage === 100;
    }

    // Special handling for BOM section - it's auto-populated from equipment sections
    // BOM is considered complete if user has synced items (checked via API, not form data)
    if (section === 'bom') {
      // BOM section doesn't have traditional required fields
      // It's validated by checking if BOM items exist via the API
      // The BOM component itself handles this validation
      // For progress purposes, we consider it optional/always complete
      return true;
    }

    // Check if all required fields in this section are filled
    const sectionFields = this.requiredFields.filter((f) => f.section === section && f.required);

    // Debug logging only in development
    if (
      process.env.NODE_ENV === 'development' &&
      (section === 'ac-equipment' || section === 'dc-works')
    ) {
      console.log(`[Progress Service Debug] ${section}:`, {
        sectionFields: sectionFields.map((f) => f.field),
        fieldValues: sectionFields.map((f) => ({
          field: f.field,
          value: formData[f.field],
          isFilled: this.isFieldFilled(formData[f.field], f.field),
        })),
      });
    }

    if (sectionFields.length === 0) {
      // No required fields in this section, consider it complete
      console.warn(`[Progress Service] No required fields found for section: ${section}`);
      return true;
    }

    const allFieldsFilled = sectionFields.every((field) =>
      this.isFieldFilled(formData[field.field], field.field)
    );

    return allFieldsFilled;
  }

  /**
   * Get list of all required fields
   */
  getRequiredFields(): RequiredField[] {
    return this.requiredFields.filter((f) => f.required);
  }

  /**
   * Get list of missing required fields
   */
  getMissingFields(formData: Record<string, any>): RequiredField[] {
    return this.requiredFields
      .filter((f) => f.required)
      .filter((field) => !this.isFieldFilled(formData[field.field], field.field));
  }

  /**
   * Validate form data for completion
   */
  validateForCompletion(formData: Record<string, any>): ValidationResult {
    const missingFields = this.getMissingFields(formData);
    const errors: string[] = [];

    if (missingFields.length > 0) {
      errors.push(`Missing ${missingFields.length} required field(s)`);
    }

    // Additional validation rules can be added here
    // For example: check if dates are valid, check if numbers are in range, etc.

    return {
      isValid: missingFields.length === 0,
      errors,
      missingFields: missingFields.map((f) => f.label),
    };
  }

  /**
   * Get progress for a specific section
   */
  getSectionProgress(
    sectionId: string,
    formData: Record<string, any>
  ): {
    completed: number;
    total: number;
    percentage: number;
  } {
    // Direct mapping - tab IDs match section names in requiredFields
    const section = sectionId;

    if (section === 'summary') {
      const progress = this.calculateProgress(formData);
      return {
        completed: progress.completedItems,
        total: progress.totalItems,
        percentage: progress.percentage,
      };
    }

    // Special handling for BOM section - it doesn't have traditional form fields
    if (section === 'bom') {
      // BOM validation is handled by the component itself via API
      // For progress display, show as complete (100%)
      return {
        completed: 1,
        total: 1,
        percentage: 100,
      };
    }

    const sectionFields = this.requiredFields.filter((f) => f.section === section && f.required);

    const completed = sectionFields.filter((field) =>
      this.isFieldFilled(formData[field.field], field.field)
    ).length;

    return {
      completed,
      total: sectionFields.length,
      percentage:
        sectionFields.length > 0 ? Math.round((completed / sectionFields.length) * 100) : 100,
    };
  }
}

// Export singleton instance
export const siteSurveyProgress = new SiteSurveyProgressService();
